// src/encounter/lib/to-immunization-request.ts
// Build the wire bodies for the IPS-rail Immunizations/Vaccines section (PROD-1267). REUSES the
// curl-verified CreateImmunizationBody / UpdateImmunizationBody contracts (patient-chart-api.ts,
// locked 2026-05-20) + the shared normalizePerformerDetails. Pure — mirrors to-procedure-request
// / to-condition-request / to-allergy-request.
//
// Actions:
//  - add    → POST /api/Patients/CreateImmunization (CreateImmunizationBody; status:'Active').
//  - edit   → PUT  /api/Patients/immunization/{id}  (UpdateImmunizationBody; round-trips record).
//  - delete → PUT  /api/Patients/immunization/{id}  (isDeleted:true; preserve everything else).
//
// ⚠️ IPS-FAITHFUL SCOPE (Flutter IPS add deliberately strips lot/expiry/site/dose vs the richer
// chart-tab add-immunization-editor.tsx). This builder captures ONLY vaccine + administered date
// + remarks; everything else ships its null/empty sentinel:
//  - vaccine: coded ImmunizationCoding (6 fields incl PascalCase `Definition`) — normalized (the
//    master rows can arrive WRAPPED as `{code:<Coding>}`, same gotcha as procedures/diagnosis).
//  - administratedDate (ISO no-Z; backend typo preserved, do NOT rename) + administratedDisplayDate.
//  - remarks (empty string default, NOT null).
//  - lotNo: "" · site: null · expiryDate/expiryDisplayDate: null · durationQuantity/durationUnits:
//    null · quantity: all-null wrapper.
//  - patient (NOT subject) = patientId · status:'Active' · dataAbsentReason:'n/a' ·
//    statusReason:'n/a' · isRx:false.
//  - CREATE stamps performer = ctx.userId, performerDetails:null, createdDate:null (server fills).
//  - UPDATE preserves the ORIGINAL performer (block edit/delete if missing — strict audit guard,
//    mirroring add-immunization-editor + the procedure asserter guard); performerDetails +
//    createdDate + encounter round-trip.
import type {
  CreateImmunizationBody,
  DoseQuantity,
  ImmunizationCoding,
  PatientImmunization,
  UpdateImmunizationBody,
} from '@/types/patient-chart-api';
import { normalizePerformerDetails } from '@/patients/components/chart/add-allergy-dialog';

export interface ImmunizationRequestContext {
  /**
   * The encounter this item is being recorded IN — stamped on CREATE as the wire `encounter`.
   *
   * FLUTTER PARITY. Its shared clinical-info popup applies `data.encounter = encounterId` to EVERY
   * summary type before dispatching per type (`popup_widget.dart:152`), and the per-tab add paths do
   * the same (`allergies_tab.dart:785`, `history_tab.dart:1318,1490` → `encounter:
   * widget.encounterId`). Nearly every patient model carries the field — allergies, immunization,
   * conditions, procedures, medications, labs, flags, medical + social history.
   *
   * PA sent a hardcoded `encounter: null` here, so an item added on the pre-encounter had no record
   * of which visit produced it. Andrew, 2026-08-28: "when i call any api in the pre-encounter for
   * allergy, vaccine, family history, social history,.... we should add encounterId value."
   *
   * Optional on purpose. These builders are ENCOUNTER-ONLY — the patients-module chart has its own
   * path (`patients/services/use-create-allergy.ts` etc.), so an earlier version of this comment
   * claiming they "also serve the chart" was wrong. The reason it stays optional is that the
   * encounter itself can lack an id: a deep-link with no `createContext`, a Booked appointment
   * (where the open-phase create is deliberately gated), or an open-phase create that failed. In
   * those the rail still renders and still writes. Absent → `null`, exactly the previous behaviour.
   */
  encounterId?: string | null;
  patientId: string;
  /** Current user's userId GUID — stamped as `performer` on CREATE. */
  userId: string;
}

/** The dialog's resolved selection (IPS-faithful 3-field). */
export interface ImmunizationFormValue {
  /** Selected vaccine code (CVX master row). */
  vaccine: ImmunizationCoding;
  /** ISO 8601 no-Z administered date. Typo (`administrated`) preserved. */
  administratedDate: string | null;
  /** Human display partner (DD/MM/YYYY). */
  administratedDisplayDate: string | null;
  remarks?: string | null;
}

export type ImmunizationAction =
  | { type: 'add'; value: ImmunizationFormValue }
  | { type: 'edit'; existing: PatientImmunization; value: ImmunizationFormValue }
  | { type: 'delete'; existing: PatientImmunization };

/** All-null `DoseQuantity` wrapper — the wire ships the wrapper (never omits) with every field
 *  literally null on IPS/chart-side immunization records (curl-verified 2026-05-20). */
const ALL_NULL_QUANTITY: DoseQuantity = { value: null, unit: null, system: null, code: null };

/**
 * Normalise an `ImmunizationCoding` row to the flat 6-field shape, defensively unwrapping the
 * `{code:<ActualCoding>}` wrapping some Master endpoints surface (VaccinationCodesList hit this —
 * Andrew 2026-05-20; same pattern as DiagnosisCodes/ProcedureCodesList). Guarantees every wire
 * field present (null when missing) so the backend doesn't reject a partial Coding. PascalCase
 * `Definition` preserved verbatim. Idempotent on already-flat input.
 */
export function normalizeVaccineCoding(
  row:
    | ImmunizationCoding
    | { code?: ImmunizationCoding | string | null; [k: string]: unknown }
    | null
    | undefined,
): ImmunizationCoding | null {
  if (!row) return null;
  const codeField = (row as { code?: unknown }).code;
  const inner =
    codeField !== null && typeof codeField === 'object'
      ? (codeField as ImmunizationCoding)
      : (row as ImmunizationCoding);
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  return {
    code: str(inner.code),
    Definition: inner.Definition ?? null,
    display: str(inner.display),
    system: str(inner.system),
    description: str(inner.description),
    shortName: str(inner.shortName),
  };
}

/** CREATE body. status always 'Active'; performer = current user; server fills performerDetails
 *  + createdDate. */
export function buildCreateImmunizationBody(
  value: ImmunizationFormValue,
  ctx: ImmunizationRequestContext,
): CreateImmunizationBody {
  return {
    identifier: null,
    vaccine: normalizeVaccineCoding(value.vaccine),
    status: 'Active',
    patient: ctx.patientId,
    encounter: ctx.encounterId ?? null,
    dataAbsentReason: 'n/a',
    performer: ctx.userId,
    // IPS add strips expiry — ship null pair.
    expiryDisplayDate: null,
    administratedDisplayDate: value.administratedDisplayDate,
    administratedDate: value.administratedDate,
    expiryDate: null,
    quantity: ALL_NULL_QUANTITY,
    statusReason: 'n/a',
    // IPS add strips site.
    site: null,
    remarks: value.remarks?.trim() || '',
    // IPS add strips lot — ship empty string (per the IPS-faithful wire-shape).
    lotNo: '',
    isDeleted: false,
    performerDetails: null,
    createdDate: null,
    isRx: false,
    // IPS add strips dose number/units.
    durationQuantity: null,
    durationUnits: null,
    speciality: null,
  };
}

/** Shared EDIT/DELETE body — round-trips the original record. The ORIGINAL performer is preserved
 *  (audit trail) — guarded non-null by buildImmunizationRequest.
 *
 *  ⚠️ INTENTIONAL (do NOT pin lot/site/expiry/duration to the create-sentinels on update): the IPS
 *  dialog only edits vaccine + administered-date + remarks, so we PRESERVE every field it doesn't
 *  manage. A record may carry lotNo/site/expiry/duration from the A&P administration flow or legacy
 *  Flutter; resetting them here would silently destroy clinician-entered data the IPS surface can't
 *  even show. This is the same field-loss guard the Medication soft-delete uses (PROD-1257, Codex
 *  HIGH — round-trip the full record, never rebuild a partial one). Create ships sentinels because
 *  there is no prior record to preserve; update/delete must not. */
function buildUpdateImmunizationBody(
  existing: PatientImmunization,
  value: ImmunizationFormValue | undefined,
  /** `encounterId`: the encounter this edit is happening IN — re-stamped onto the record,
   *  Flutter-style (see the field note below). Absent outside an encounter. */
  opts: { isDeleted: boolean; encounterId?: string | null },
): UpdateImmunizationBody {
  // Preserve the original (richer) values for any field the IPS dialog doesn't edit.
  const vaccine = value
    ? normalizeVaccineCoding(value.vaccine)
    : normalizeVaccineCoding(existing.vaccine);
  return {
    identifier: existing.identifier!,
    vaccine,
    status: 'Active',
    patient: existing.patient ?? '',
    /**
     * The CURRENT encounter wins, falling back to the record's own.
     *
     * FLUTTER PARITY (Andrew, 2026-08-28: "we will do as flutter"). Its shared popup assigns
     * `data.encounter = encounterId` BEFORE branching on `identifier != null`
     * (`popup_widget.dart:152`), so an edit re-stamps the item with the encounter it is being
     * edited IN — not the one that first recorded it. An earlier pass here preserved
     * `existing.encounter` on the reasoning that editing an old allergy shouldn't re-attribute it;
     * that was my call, and Andrew overruled it. It also fixes items PA wrote before the create-path
     * fix: they carry `encounter: null` and the next edit now heals them, where round-tripping would
     * have kept them null forever.
     *
     * THE `?? existing.encounter` IS NOT DECORATION. Flutter can assign unconditionally because its
     * popup guarantees an id first (`if (isBlank(encounterId)) createEncounter()`, initState). We
     * have no such guarantee: a deep-link with no `createContext`, a Booked appointment (open-phase
     * create gated), or a failed open-phase create all leave `encounterId` null while the rail still
     * renders and still writes. Assigning unconditionally in those cases would write null over a
     * real link and DESTROY the record's provenance. So: re-stamp when we have an encounter,
     * preserve when we don't.
     *
     * (An earlier version of this note justified the fallback with the patients-module chart. That
     * was wrong — the chart has its own request path and never calls these builders.)
     */
    encounter: opts.encounterId ?? existing.encounter ?? null,
    dataAbsentReason: 'n/a',
    // Original recorder preserved — guaranteed non-null by buildImmunizationRequest.
    performer: existing.performer!,
    expiryDisplayDate: existing.expiryDisplayDate ?? null,
    administratedDisplayDate: value ? value.administratedDisplayDate : existing.administratedDisplayDate ?? null,
    administratedDate: value ? value.administratedDate : existing.administratedDate ?? null,
    expiryDate: existing.expiryDate ?? null,
    quantity: ALL_NULL_QUANTITY,
    statusReason: 'n/a',
    site: normalizeVaccineCoding(existing.site),
    remarks: value ? value.remarks?.trim() || '' : existing.remarks ?? '',
    lotNo: existing.lotNo ?? '',
    isDeleted: opts.isDeleted,
    performerDetails: normalizePerformerDetails(existing.performerDetails),
    createdDate: existing.createdDate ?? null,
    isRx: existing.isRx === true,
    durationQuantity: normalizeVaccineCoding(existing.durationQuantity),
    durationUnits: normalizeVaccineCoding(existing.durationUnits),
    speciality: null,
  };
}

export interface ImmunizationRequest {
  kind: 'create' | 'update';
  /** Immunization GUID for the update path (`PUT /immunization/{id}`); undefined on create. */
  immunizationId?: string;
  body: CreateImmunizationBody | UpdateImmunizationBody;
}

/**
 * Resolve an immunization action into endpoint + body.
 *  - add    → CREATE (always Active).
 *  - edit   → UPDATE.
 *  - delete → UPDATE soft-delete.
 * Returns null when the action is structurally invalid: edit/delete without an identifier, or
 * without the original `performer` (which the update body must preserve — refuse to ship null
 * rather than substituting the editing user, mirroring add-immunization-editor's strict guard).
 */
export function buildImmunizationRequest(
  action: ImmunizationAction,
  ctx: ImmunizationRequestContext,
): ImmunizationRequest | null {
  if (action.type === 'add') {
    return { kind: 'create', body: buildCreateImmunizationBody(action.value, ctx) };
  }
  // edit/delete must carry an identifier AND the original performer to round-trip safely.
  if (!action.existing.identifier || !action.existing.performer) return null;

  if (action.type === 'delete') {
    return {
      kind: 'update',
      immunizationId: action.existing.identifier,
      body: buildUpdateImmunizationBody(action.existing, undefined, { isDeleted: true, encounterId: ctx.encounterId }),
    };
  }

  return {
    kind: 'update',
    immunizationId: action.existing.identifier,
    body: buildUpdateImmunizationBody(action.existing, action.value, { isDeleted: false, encounterId: ctx.encounterId }),
  };
}
